/**
 * D-089 (Dashboard-Anteil) — EINE Quelle für Berichts- und Vorjahr.
 *
 * Seit D-089 exportiert das SR-Repo `berichtsjahr` / `vorjahr` im Snapshot
 * (additiv, via /api/meta). Die Schema-Schlüssel q1_2026 … / vorjahr_2025 /
 * jahr_2026 bleiben als Vertrag UNVERÄNDERT; sie bedeuten „Quartal/Jahr des
 * Berichtsjahrs" bzw. „Vorjahr". Beschriftungen nehmen die Jahreszahl daher
 * ausschließlich von hier — nie mehr hart kodiert.
 *
 * Fallback (alter Snapshot ohne die Felder): berichtsjahr 2026, vorjahr
 * berichtsjahr - 1. Ungültige Werte (externe, ungeprüfte Blob-Daten) fallen
 * ebenfalls auf den Fallback zurück, statt Unsinn in ein Label zu schreiben.
 *
 * Verteilung: Layout lädt Meta und stellt die Jahre per <JahreProvider> bereit;
 * Komponenten lesen sie mit useJahre(). Ohne Provider (isolierte Komponenten-
 * Tests) gilt der Fallback.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { Meta } from '../types/snapshot';

export interface Jahre {
  /** Berichtsjahr (Quartale q1…q4, Prognose, Soll/Ist). */
  bj: number;
  /** Vorjahr (VJ-Spalten, JA-Basis des Q-Soll). */
  vj: number;
}

/** Fallback für Snapshots vor D-089 — der einzige Ort mit festen Jahreszahlen. */
export const JAHRE_FALLBACK: Jahre = Object.freeze({ bj: 2026, vj: 2025 });

/** Plausibles Kalenderjahr? Schützt Labels vor '2027' (String), 2026.5, NaN, 27. */
function istJahr(x: unknown): x is number {
  return typeof x === 'number' && Number.isInteger(x) && x >= 2000 && x <= 2100;
}

/** Berichts-/Vorjahr aus Meta; fehlende oder ungültige Felder → Fallback. */
export function jahreAusMeta(
  meta: Pick<Meta, 'berichtsjahr' | 'vorjahr'> | null | undefined,
): Jahre {
  const bj = istJahr(meta?.berichtsjahr) ? meta.berichtsjahr : JAHRE_FALLBACK.bj;
  const vj = istJahr(meta?.vorjahr) ? meta.vorjahr : bj - 1;
  return { bj, vj };
}

const JahreContext = createContext<Jahre>(JAHRE_FALLBACK);

export function JahreProvider({ jahre, children }: { jahre: Jahre; children: ReactNode }) {
  return <JahreContext.Provider value={jahre}>{children}</JahreContext.Provider>;
}

/** Berichts-/Vorjahr für Beschriftungen. Ohne Provider: Fallback 2026/2025. */
export function useJahre(): Jahre {
  return useContext(JahreContext);
}
